import { useFocusEffect } from '@react-navigation/native'
import {
    Dispatch,
    FC,
    ReactNode,
    SetStateAction,
    createContext,
    useCallback,
    useContext,
    useState,
} from 'react'

export interface MainHeaderOverrides {
    title?: string
    left?: ReactNode
    right?: ReactNode
}

interface MainHeaderOverridesContextValue {
    overrides: MainHeaderOverrides | undefined
    setOverrides: Dispatch<SetStateAction<MainHeaderOverrides | undefined>>
}

const MainHeaderOverridesContext = createContext<MainHeaderOverridesContextValue | undefined>(
    undefined
)

const MainHeaderOverridesProvider: FC<{ children: ReactNode }> = ({ children }) => {
    const [overrides, setOverrides] = useState<MainHeaderOverrides>()

    return (
        <MainHeaderOverridesContext.Provider value={{ overrides, setOverrides }}>
            {children}
        </MainHeaderOverridesContext.Provider>
    )
}

export default MainHeaderOverridesProvider

export const useMainHeaderOverrides = (nextOverrides?: MainHeaderOverrides) => {
    const context = useContext(MainHeaderOverridesContext)

    if (!context) {
        throw new Error('useMainHeaderOverrides must be used inside MainHeaderOverridesProvider')
    }

    const { setOverrides } = context

    useFocusEffect(
        useCallback(() => {
            if (!nextOverrides) return

            setOverrides(nextOverrides)
            return () =>
                setOverrides((current) => (current === nextOverrides ? undefined : current))
        }, [nextOverrides, setOverrides])
    )
}

export const useCurrentMainHeaderOverrides = () => {
    const context = useContext(MainHeaderOverridesContext)

    if (!context) {
        throw new Error(
            'useCurrentMainHeaderOverrides must be used inside MainHeaderOverridesProvider'
        )
    }

    return context.overrides
}
